import React from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Button, Modal, toast, useWizard } from "@picklebet/ui";
import { constructCurrency } from "common/currency";
import { parseError } from "@/utilities/errorsParser";
import { useWithdrawAmountValidationSchema } from "@/features/wallet/hooks/useWithdrawAmountValidationSchema";
import { useGameSessions } from "@/features/casino/hooks/useGameSessions";
import {
  MethodRow,
  MethodRowSkeleton,
} from "@/features/wallet/components/deposit/components/deposit-wizard/method-row/MethodRow";
import { SkrillMethodIcon } from "@/features/wallet/components/deposit/components/deposit-wizard/method-row/SkrillMethodIcon";
import { AmountInputSkeleton } from "@/features/wallet/components/shared/amount-input-skeleton/AmountInputSkeleton";
import { useReportSubmitting } from "@/features/wallet/hooks/useReportSubmitting";
import { useWithdrawWizard } from "@/features/wallet/components/withdraw/hooks/useWithdrawWizard";
import { useSkrillWithdrawal } from "@/features/wallet/components/withdraw/hooks/useSkrillWithdrawal";
import { useWithdrawalWindowAlertDismissed } from "@/features/wallet/components/withdraw/hooks/useWithdrawalWindowAlertDismissed";
import type { WithdrawStepName } from "@/features/wallet/components/withdraw/utils/registry";
import { WithdrawContent } from "../../blocks/withdraw-content/WithdrawContent";
import { WithdrawAmountInput } from "../../blocks/withdraw-amount-input/WithdrawAmountInput";
import {
  WithdrawableBalance,
  WithdrawableBalanceSkeleton,
} from "../../blocks/withdrawable-balance/WithdrawableBalance";
import { ActiveSessionAlert } from "../../blocks/active-session-alert/ActiveSessionAlert";
import { SectionDivider } from "../../blocks/section-divider/SectionDivider";
import { SectionHeader } from "../../blocks/section-header/SectionHeader";

import * as styles from "./SkrillWithdrawView.module.scss";

// mirrors the settled view block for block: any row missing here becomes a
// jump in modal height when the data lands
export const SkrillWithdrawViewSkeleton = () => (
  <WithdrawContent>
    <AmountInputSkeleton />
    <SectionDivider />
    <SectionHeader>Skrill Account</SectionHeader>
    <MethodRowSkeleton />
    <Modal.Footer>
      <Button className={styles.withdrawButton} skeleton>
        Withdraw
      </Button>
    </Modal.Footer>
    <WithdrawableBalanceSkeleton />
  </WithdrawContent>
);

export const SkrillWithdrawView = () => {
  const {
    amount,
    setAmount,
    currency,
    setSubmitting,
    mustVerifyToWithdraw,
    showVerificationRequired,
  } = useWithdrawWizard();
  const { push } = useWizard<WithdrawStepName>();
  const { hasActiveGameSession } = useGameSessions(true);
  const {
    submit,
    selectedEmail,
    emails,
    isLoading: emailsLoading,
  } = useSkrillWithdrawal();

  const [schema, schemaLoading] = useWithdrawAmountValidationSchema();
  const [alertDismissed, setAlertDismissed] =
    useWithdrawalWindowAlertDismissed();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { amount },
  });

  useReportSubmitting(isSubmitting, setSubmitting);

  const onSubmit = async (values: { amount: number }) => {
    if (mustVerifyToWithdraw) {
      showVerificationRequired();
      return;
    }

    const result = await submit(values.amount);

    if (!result.ok) {
      toast({
        variant: "danger",
        title: "Withdrawal Failed",
        description: parseError(result.error),
      });
    }
  };

  if (schemaLoading || emailsLoading) {
    return <SkrillWithdrawViewSkeleton />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <WithdrawContent>
        <Controller
          control={control}
          name="amount"
          render={({ field: { onChange, value } }) => (
            <WithdrawAmountInput
              value={value}
              onChange={(next) => {
                onChange(next);
                setAmount(next);
              }}
              errorMessage={errors.amount?.message}
            />
          )}
        />

        <SectionDivider />

        <SectionHeader>Skrill Account</SectionHeader>

        <MethodRow
          icon={<SkrillMethodIcon />}
          label={selectedEmail ?? "No Skrill account available"}
          trailing={
            emails.length > 1 && (
              <Button size="sm" onClick={() => push("withdraw/skrill-email")}>
                Change
              </Button>
            )
          }
        />

        <ActiveSessionAlert />

        <Modal.Footer>
          <Button
            className={styles.withdrawButton}
            type="submit"
            disabled={
              !amount || !selectedEmail || hasActiveGameSession || isSubmitting
            }
            loading={isSubmitting}
          >
            Withdraw{" "}
            {constructCurrency(amount * 100, { currencyType: currency })}
          </Button>
        </Modal.Footer>

        <WithdrawableBalance />

        {!alertDismissed && (
          <Alert
            variant="info"
            size="small"
            onDismiss={() => setAlertDismissed(true)}
          >
            Withdrawals are processed multiple times per day, 7 days a week.
          </Alert>
        )}
      </WithdrawContent>
    </form>
  );
};
